import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { MoreHorizontal, ListChecks, Pencil, ArrowRightLeft, Trash2 } from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Button } from './ui/Button'
import { TextField } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { CONTAINS_LETTER } from '../lib/validators'
import { FolderMoveDialog } from './FolderMoveDialog'
import { ListCreateDialog } from './ListCreateDialog'
import { renameFavoriteFolderMeta } from '../lib/space-prefs'
import { useAuthStore } from '../stores/authStore'

const renameSchema = z.object({
  name: z
    .string({ message: 'Project Name is required.' })
    .trim()
    .min(1, 'Project Name is required.')
    .min(2, 'Project Name must be at least 2 characters.')
    .max(100, 'Project Name must be at most 100 characters.')
    .regex(CONTAINS_LETTER, 'Project Name must contain at least one letter.'),
})

export function FolderRowActions({ folder, workspaceId }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [listCreateOpen, setListCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [coords, setCoords] = useState(null)

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const r = triggerRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.top, left: r.right + 4 })
  }, [open])
  useLayoutEffect(() => {
    if (!open || !coords) return
    const menu = menuRef.current
    const r = triggerRef.current?.getBoundingClientRect()
    if (!menu || !r) return
    const margin = 8
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    let top = r.top
    if (top + menu.offsetHeight > viewportH - margin) {
      top = Math.max(margin, viewportH - margin - menu.offsetHeight)
    }
    // Default opens to the RIGHT of the trigger; flip to the LEFT when that
    // would overflow the viewport (trigger near the right edge on mobile),
    // then clamp so it never runs off either edge.
    let left = r.right + 4
    if (left + menu.offsetWidth > viewportW - margin) {
      left = r.left - menu.offsetWidth - 4
    }
    left = Math.max(margin, Math.min(left, viewportW - menu.offsetWidth - margin))
    if (Math.abs(top - coords.top) > 1 || Math.abs(left - coords.left) > 1) {
      setCoords({ top, left })
    }
  }, [open, coords])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const renameForm = useForm({
    resolver: zodResolver(renameSchema),
    defaultValues: { name: folder.name },
  })
  useEffect(() => {
    if (renameOpen) renameForm.reset({ name: folder.name })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameOpen, folder.name])

  const renameMutation = useMutation({
    mutationFn: async (v) => {
      await api.patch(`/spaces/${folder.spaceId}/folders/${folder.id}`, {
        name: v.name.trim(),
      })
    },
    // Capture the pre-rename name so the Favourites refresh below can match
    // favourites still labelled with the old Project name (race-proof).
    onMutate: () => ({ previousName: folder.name }),
    onSuccess: (_data, v, ctx) => {
      toast.success('Project renamed successfully.')
      setRenameOpen(false)
      void qc.invalidateQueries({ queryKey: ['folders', folder.spaceId] })
      // A favourited list inside this Project shows the Project's name in the
      // sidebar; cascade the rename so those favourites don't keep the old
      // name. No-ops when nothing inside the Project is favourited.
      renameFavoriteFolderMeta(
        useAuthStore.getState().user?.id,
        folder.id,
        folder.spaceId,
        ctx?.previousName,
        v.name.trim(),
      )
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields?.name) renameForm.setError('name', { message: fields.name })
      else toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/spaces/${folder.spaceId}/folders/${folder.id}`)
    },
    onSuccess: () => {
      toast.success('Project deleted successfully.')
      setConfirmDelete(false)
      void qc.invalidateQueries({ queryKey: ['folders', folder.spaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        aria-label="Project actions"
        title="Project actions"
        className="p-1 rounded text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-2)] cursor-pointer"
      >
        <MoreHorizontal size={14} />
      </button>

      {open && coords
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 60,
              }}
            >
              <Menu
                ref={menuRef}
                onNewList={() => {
                  setOpen(false)
                  setListCreateOpen(true)
                }}
                onRename={() => {
                  setOpen(false)
                  setRenameOpen(true)
                }}
                onMoveTo={() => {
                  setOpen(false)
                  setMoveOpen(true)
                }}
                onDelete={() => {
                  setOpen(false)
                  setConfirmDelete(true)
                }}
              />
            </div>,
            document.body,
          )
        : null}

      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename Project"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setRenameOpen(false)}
              disabled={renameMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={renameForm.handleSubmit((v) => renameMutation.mutate(v))}
              loading={renameMutation.isPending}
            >
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={renameForm.handleSubmit((v) => renameMutation.mutate(v))}>
          <TextField
            label="Project Name"
            required
            placeholder="Enter a Project Name"
            maxLength={100}
            error={renameForm.formState.errors.name?.message}
            {...renameForm.register('name')}
          />
        </form>
      </Modal>

      <FolderMoveDialog
        open={moveOpen}
        folder={folder}
        workspaceId={workspaceId}
        onClose={() => setMoveOpen(false)}
      />

      {/* Create a List inside THIS Project — the Project is pre-selected
       *  in the dialog so the new List lands here. */}
      <ListCreateDialog
        open={listCreateOpen}
        defaultSpaceId={folder.spaceId}
        defaultFolderId={folder.id}
        onClose={() => setListCreateOpen(false)}
        onCreated={(listId, spaceId) =>
          navigate(`/app/spaces/${spaceId}/lists/${listId}`)
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${folder.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  )
}

const Menu = forwardRef(function Menu(props, ref) {
  return (
    <div
      ref={ref}
      role="menu"
      className="w-56 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]"
    >
      <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        Project Settings
      </p>
      <Group>
        <Item icon={ListChecks} label="New List" onClick={props.onNewList} />
        <Item icon={Pencil} label="Rename" onClick={props.onRename} />
        <Item icon={ArrowRightLeft} label="Move to" onClick={props.onMoveTo} />
      </Group>
      <Divider />
      <Group>
        <Item icon={Trash2} label="Delete" danger onClick={props.onDelete} />
      </Group>
    </div>
  )
})

function Group({ children }) {
  return <div className="py-1">{children}</div>
}
function Divider() {
  return <div className="border-t border-[color:var(--color-rule)]" aria-hidden="true" />
}

function Item({ icon: Icon, label, danger, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={
        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[color:var(--color-card-hover)] cursor-pointer ' +
        (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink)]')
      }
    >
      <Icon
        size={14}
        className={
          'shrink-0 ' +
          (danger ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-muted)]')
        }
      />
      <span className="flex-1 min-w-0 truncate font-medium">{label}</span>
    </button>
  )
}
